import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Database, GitMerge, RefreshCw, Wand2, Users } from 'lucide-react';
import { getApiUrl } from '../utils/api';
import { PageHeader, KpiCard, StatusPill, EmptyState, TableRowsSkeleton } from '../components/ds';
import { Button } from '../components/ui/button';

const API_URL = getApiUrl();
const CHECK_LABELS = {
  missing_normalized_phone: 'Leads without normalized phone',
  sales_rep_field_split: 'Sales-rep field out of sync (legacy vs canonical)',
  trial_flag_without_payment: 'Trial flag PAID but no trial payment',
  trial_payment_without_flag: 'Trial payment exists but flag not PAID',
  enrolled_flag_without_payment: 'Enrolled flag but no enrolment payment',
  enrolment_payment_without_flag: 'Enrolment payment but not flagged enrolled',
  lead_status_missing: 'Leads with empty lead_status',
  enrollment_status_missing: 'Leads with empty enrollment_status',
  orphan_paid_payments: 'Paid payments whose lead no longer exists',
  undated_paid_payments: 'Paid payments with no captured_at / created_at (invisible to every date filter)',
};

export default function DataIntegrityPage() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [healing, setHealing] = useState(false);
  const [merging, setMerging] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API_URL}/api/data-integrity/report`, { withCredentials: true });
      setReport(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load report');
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const heal = async (checks, dryRun) => {
    setHealing(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/data-integrity/heal`, { checks, dry_run: dryRun }, { withCredentials: true });
      const n = Object.values(data.result).reduce((a, r) => a + (r.updated ?? r.would_update ?? 0), 0);
      toast.success(dryRun ? `Dry run: ${n} lead(s) would be updated` : `Healed ${n} lead(s)`);
      if (!dryRun) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Heal failed');
    } finally {
      setHealing(false);
    }
  };

  const merge = async (group) => {
    const [primary, ...dups] = group.leads;
    setMerging(group.key);
    try {
      const body = { primary_lead_id: primary.lead_id, duplicate_lead_ids: dups.map((d) => d.lead_id), dry_run: false };
      const { data } = await axios.post(`${API_URL}/api/data-integrity/merge`, body, { withCredentials: true });
      toast.success(`Merged ${data.removed.length} duplicate(s) into ${primary.full_name || primary.lead_id}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Merge failed');
    } finally {
      setMerging(null);
    }
  };

  const mergeAllPhones = async (dryRun) => {
    if (!dryRun && !window.confirm('Merge every group of leads that share a phone number into the oldest lead? Payments, bookings and invoices are re-linked and the extra emails become aliases.')) return;
    setMerging('all-phones');
    try {
      const { data } = await axios.post(`${API_URL}/api/data-repair/merge-duplicate-phone-leads`,
        { dry_run: dryRun }, { withCredentials: true, timeout: 300000 });
      toast.success(dryRun
        ? `${data.duplicate_leads} duplicate lead(s) across ${data.phone_groups_with_duplicates} number(s)`
        : `Merged ${data.merged} duplicate lead(s)${data.unique_index_installed ? ' — unique phone lock now active' : ''}`);
      if (!dryRun) load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Phone merge failed');
    } finally {
      setMerging(null);
    }
  };

  const healable = (report?.checks || []).filter((c) => c.healable && c.count > 0).map((c) => c.id);
  const totalIssues = (report?.checks || []).reduce((a, c) => a + c.count, 0);

  return (
    <div className="space-y-6" data-testid="data-integrity-page">
      <PageHeader
        title="Data Integrity"
        description="Drift between lead flags and the payments ledger, identity hygiene and duplicate customers. Healers are idempotent; the scheduler runs the safe ones every 6 hours."
        actions={(
          <>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="integrity-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
            <Button variant="outline" onClick={() => heal(healable, true)} disabled={healing || !healable.length} data-testid="integrity-dry-run-btn">
              Dry run
            </Button>
            <Button onClick={() => heal(healable, false)} disabled={healing || !healable.length} data-testid="integrity-heal-all-btn">
              <Wand2 className="h-4 w-4 mr-2" /> Heal all safe checks
            </Button>
            <Button variant="outline" onClick={() => mergeAllPhones(true)} disabled={!!merging} data-testid="merge-phones-dryrun-btn">
              Check phone duplicates
            </Button>
            <Button onClick={() => mergeAllPhones(false)} disabled={!!merging} data-testid="merge-phones-btn">
              <GitMerge className="h-4 w-4 mr-2" /> Merge all same-number leads
            </Button>
          </>
        )}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 stagger">
        <KpiCard title="Leads" value={report?.totals?.leads ?? '—'} icon={Users} tone="teal" testId="kpi-card-integrity-leads" />
        <KpiCard title="Paid payments" value={report?.totals?.paid_payments ?? '—'} icon={Database} tone="primary" testId="kpi-card-integrity-payments" />
        <KpiCard title="Open issues" value={report ? totalIssues : '—'} icon={Wand2} tone={totalIssues ? 'warning' : 'success'} subtitle={`${healable.length} check(s) auto-healable`} testId="kpi-card-integrity-issues" />
        <KpiCard title="Duplicate groups" value={report?.totals?.duplicate_groups ?? '—'} icon={GitMerge} tone={report?.totals?.duplicate_groups ? 'warning' : 'success'} testId="kpi-card-integrity-duplicates" />
      </div>

      <section className="card">
        <div className="card-header flex items-center justify-between py-4">
          <h2 className="font-display text-base md:text-lg font-semibold">Checks</h2>
        </div>
        {loading && !report ? <TableRowsSkeleton rows={6} cols={4} /> : (
          <table className="w-full">
            <thead className="table-header">
              <tr><th className="px-4 py-2.5 text-left">Check</th><th className="px-4 py-2.5 text-right">Count</th><th className="px-4 py-2.5 text-left">Sample lead / payment IDs</th><th className="px-4 py-2.5 text-right">Action</th></tr>
            </thead>
            <tbody>
              {report?.checks.map((c) => (
                <tr key={c.id} className="table-row" data-testid={`integrity-check-${c.id}`}>
                  <td className="table-cell font-medium">{CHECK_LABELS[c.id] || c.id}</td>
                  <td className="table-cell text-right"><StatusPill status={c.count ? (c.healable ? 'pending' : 'drift') : 'ok'} label={String(c.count)} testId={`integrity-count-${c.id}`} /></td>
                  <td className="table-cell font-mono text-[11px] text-slate-500 truncate max-w-md">{c.sample.join(', ') || '—'}</td>
                  <td className="table-cell text-right">
                    {c.healable && c.count > 0 ? (
                      <Button size="sm" variant="outline" onClick={() => heal([c.id], false)} disabled={healing} data-testid={`integrity-heal-${c.id}`}>Heal</Button>
                    ) : <span className="text-xs text-muted-foreground">{c.count ? 'Review manually' : '—'}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      <section className="card">
        <div className="card-header py-4">
          <h2 className="font-display text-base md:text-lg font-semibold">Duplicate customers</h2>
          <p className="text-xs text-muted-foreground mt-0.5">Grouped by phone (last 10 digits) or email. The oldest lead is kept; payments, bookings and logs are relinked to it.</p>
        </div>
        <div className="card-body pt-4">
          {report && report.duplicates.length === 0 && <EmptyState title="No duplicate customers" hint="Every phone and email maps to exactly one lead." />}
          <div className="space-y-3">
            {report?.duplicates.map((g) => (
              <div key={`${g.match_on}-${g.key}`} className="rounded-lg border border-border p-3 flex flex-col md:flex-row md:items-center gap-3" data-testid={`dup-group-${g.key}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-muted-foreground">Match on <span className="font-mono">{g.match_on}: {g.key}</span></p>
                  <div className="mt-1 flex flex-wrap gap-2">
                    {g.leads.map((l, i) => (
                      <span key={l.lead_id} className={`inline-flex items-center gap-1.5 rounded-md border px-2 py-1 text-xs ${i === 0 ? 'border-emerald-300 bg-emerald-50' : 'border-border bg-muted'}`}>
                        <span className="font-medium">{l.full_name || 'Unnamed'}</span>
                        <span className="font-mono text-[10px] text-slate-500">{l.lead_id}</span>
                        <StatusPill status={l.lead_status || 'new'} className="!py-0" />
                        {i === 0 && <span className="text-[10px] font-semibold text-emerald-700">KEEP</span>}
                      </span>
                    ))}
                  </div>
                </div>
                <Button size="sm" onClick={() => merge(g)} disabled={merging === g.key} data-testid={`dup-merge-${g.key}`}>
                  <GitMerge className="h-4 w-4 mr-1.5" /> Merge into oldest
                </Button>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
